/*
 * Pre-checkout del mini-cart: hoja que sube desde abajo al pulsar «Ir a pagar».
 *   Paso 1: envío a domicilio o retiro en tienda.
 *   Paso 2: ubigeo (departamento / provincia / distrito) o tienda de retiro, más documento
 *           de identidad y «Necesito factura» (RUC), con la validación de /cart.
 * Replica el flujo de enova-shipping-carrito.js (página /cart):
 *   1. Ubigeos, tarifa y tiendas desde el app proxy (departaments/provinces/distrits,
 *      district-calc-rates y shop-addresses).
 *   2. cart/update.js con los note_attributes estándar (region, province, district, ubigeo…).
 *   3. cartCreate en la Storefront API con los atributos por línea que lee el CarrierService
 *      (_dispatch_mode, _shipping_method, _ubigeo, _peso, _sku), la dirección precargada
 *      (envío) o la preferencia de retiro con la sucursal elegida (pickupHandle).
 *   4. Redirección al checkout (+ UTMs). Si algo falla, cae al checkout nativo.
 * Comparte con /cart la clave `customerData` de localStorage, así lo elegido en un lado
 * aparece precargado en el otro.
 */
(() => {
  const CFG = window.PRECHECKOUT_CONFIG;
  if (!CFG) return;

  const APP_PROXY = CFG.appProxy || 'apps/apolo-ubigeo-calculation';
  const COUNTRY = CFG.country || 'PE';
  const COUNTRY_PROXY_ID = '1';
  const CUSTOMER_KEY = 'customerData';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  const STOREFRONT_URL = `https://${CFG.storeDomain}/api/${CFG.apiVersion}/graphql.json`;
  // Bloquear tiendas sin stock del carrito. Apagado por defecto: el stock por sucursal del
  // backend aún no coincide con lo que ofrece el checkout. El stock se consulta igual para
  // obtener el ID de cada location (preselección de la tienda en el checkout).
  const PICKUP_STOCK_VALIDATION = CFG.pickupStockValidation === true;
  // Lima Metropolitana (1501) y Callao (0701) vs. provincias. Editable en PRECHECKOUT_CONFIG.
  const DELIVERY_TIMES = Array.isArray(CFG.deliveryTimes)
    ? CFG.deliveryTimes
    : [
        { ubigeo_prefix: ['1501', '0701'], text: '1 a 4 días hábiles' },
        { ubigeo_prefix: '*', text: '3 a 7 días hábiles' },
      ];

  // Mismo default que enova-shipping-carrito.js; el backend puede sobreescribirlo (addressMapping).
  const DEFAULT_ADDRESS_MAPPING = {
    firstName: 'first_name',
    lastName: 'last_name',
    phone: 'phone',
    province: 'region',
    city: 'province',
    address1: 'address',
    address2: 'reference',
    zip: 'ubigeo',
  };

  // Tipos de documento y RUC: los define el backend (?scope=settings › documents[COUNTRY]),
  // igual que en /cart. Esto es solo el respaldo si el backend no responde.
  const DEFAULT_DOCUMENTS = {
    person: [
      { label: 'DNI', value: 'dni', minLen: 8, maxLen: 8, numeric: true },
      { label: 'Carnet de Extranjería', value: 'ce', minLen: 8, maxLen: 12, numeric: false },
    ],
    company: { label: 'RUC', value: 'ruc', minLen: 11, maxLen: 11, numeric: true },
  };

  function deriveDocuments(documents) {
    const config = documents && documents[COUNTRY];
    if (!config || !Array.isArray(config.person) || !config.person.length) return null;
    return { person: config.person, company: config.company || null };
  }

  // Deja solo los caracteres que admite el documento (dígitos o alfanuméricos) y su largo máximo.
  // Se aplica en `input` y no en `keypress`: los teclados de Android no disparan keypress.
  function sanitizeDocument(value, doc) {
    if (!doc) return String(value || '');
    const pattern = doc.numeric === false ? /[^a-zA-Z0-9]/g : /[^0-9]/g;
    const clean = String(value || '').replace(pattern, '');
    return doc.maxLen ? clean.slice(0, doc.maxLen) : clean;
  }

  function isDocumentValid(value, doc) {
    if (!doc) return String(value || '').trim() !== '';
    const pattern = doc.numeric === false ? /^[a-zA-Z0-9]+$/ : /^[0-9]+$/;
    return value.length >= doc.minLen && value.length <= doc.maxLen && pattern.test(value);
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function proxyUrl(query) {
    return `${root()}${APP_PROXY}?${query}`;
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
  }

  // Respuestas del app proxy que no dependen del carrito (config, ubigeos, tiendas): se guardan
  // en sessionStorage para que la precarga en cada página no repita peticiones al backend.
  const PROXY_CACHE_PREFIX = 'enova-minicart:';

  function getProxyJSON(query, { cache = false } = {}) {
    if (cache) {
      try {
        const hit = sessionStorage.getItem(PROXY_CACHE_PREFIX + query);
        if (hit) return Promise.resolve(JSON.parse(hit));
      } catch (error) {
        /* sin storage: se pide al backend */
      }
    }
    return getJSON(proxyUrl(query)).then((data) => {
      if (cache) {
        try {
          sessionStorage.setItem(PROXY_CACHE_PREFIX + query, JSON.stringify(data));
        } catch (error) {
          /* noop */
        }
      }
      return data;
    });
  }

  function storefront(query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': CFG.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    }).then((response) => response.json());
  }

  function readCustomerData() {
    try {
      return JSON.parse(window.localStorage.getItem(CUSTOMER_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCustomerData(values) {
    try {
      const data = Object.assign(readCustomerData(), values);
      window.localStorage.setItem(CUSTOMER_KEY, JSON.stringify(data));
    } catch (error) {
      /* sin storage: el flujo sigue igual */
    }
  }

  // Los UTMs los guarda en sessionStorage el precheckout de /cart; aquí también se capturan
  // porque el mini-cart puede abrirse en cualquier página.
  function captureUTMs() {
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value;
      });
      if (Object.keys(utms).length) sessionStorage.setItem('utm_params', JSON.stringify(utms));
    } catch (error) {
      /* noop */
    }
  }

  function appendUTMs(url) {
    let utms = {};
    try {
      utms = JSON.parse(sessionStorage.getItem('utm_params')) || {};
    } catch (error) {
      utms = {};
    }
    const params = Object.keys(utms).map((key) => `${key}=${encodeURIComponent(utms[key])}`);
    if (!params.length) return url;
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  function getAppliedDiscountCode(cart) {
    const apps = (cart && cart.cart_level_discount_applications) || [];
    const byCode = apps.find((app) => app && app.type === 'discount_code' && app.title);
    if (byCode) return byCode.title;
    const codes = (cart && cart.discount_codes) || [];
    const applied = codes.find((code) => code && code.applicable !== false) || codes[0];
    return applied ? applied.code : null;
  }

  function toProvinceCode(region) {
    return region === 'Lima' || region === 'LIMA' ? 'PE-LMA' : region;
  }

  function buildDeliveryAddress(attributes, mapping) {
    const address = {};
    Object.keys(mapping).forEach((shopifyField) => {
      const source = mapping[shopifyField];
      if (!source) return;
      const candidates = Array.isArray(source) ? source : [source];
      const key = candidates.find((candidate) => candidate && String(attributes[candidate] || '').trim() !== '');
      if (!key) return;
      let value = String(attributes[key]).trim();
      if (shopifyField === 'province') value = toProvinceCode(value);
      address[shopifyField] = value;
    });
    if (!address.country) address.country = COUNTRY;
    return address;
  }

  // Dirección de facturación por la URL del checkout (checkout[billing_address][…]): la Cart API
  // no tiene billingAddress, y en retiro la dirección del carrito no llega a facturación. El
  // checkout nativo sí toma estos parámetros y mantiene la pestaña «Retiro».
  const BILLING_PARAMS = {
    firstName: 'first_name',
    lastName: 'last_name',
    company: 'company',
    address1: 'address1',
    address2: 'address2',
    city: 'city',
    province: 'province',
    country: 'country',
    zip: 'zip',
    phone: 'phone',
  };

  function appendBillingAddress(url, address) {
    if (!address) return url;
    const params = Object.keys(BILLING_PARAMS)
      .filter((field) => address[field] && field !== 'country')
      .map((field) => `checkout[billing_address][${BILLING_PARAMS[field]}]=${encodeURIComponent(address[field])}`);
    if (!params.length) return url;
    params.push(`checkout[billing_address][country]=${encodeURIComponent(address.country || COUNTRY)}`);
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // pickupHandle espera el ID numérico de la location ("93764780252"), no el GID
  // (ver shopify.dev › Storefront API › Cart › preferences.delivery.pickupHandle).
  function toLocationId(id) {
    return String(id || '').trim().split('/').pop();
  }

  function matchesUbigeo(prefix, ubigeo) {
    const prefixes = Array.isArray(prefix) ? prefix : [prefix];
    return prefixes.some((item) => item === '*' || String(ubigeo).startsWith(String(item)));
  }

  // Descripción del método para el ubigeo, con la misma prioridad que /cart:
  //   1. display_rules del backend (con corte horario si aplica),
  //   2. description del backend,
  //   3. tiempos de envío por zona (DELIVERY_TIMES).
  // Es el texto que se muestra y el que viaja al checkout en _dispatch_mode_description.
  function resolveMethodDescription(method, ubigeo) {
    const rules = method.display_rules && method.display_rules.rules;
    if (Array.isArray(rules) && rules.length) {
      const rule = rules.find((item) => matchesUbigeo(item.ubigeo_prefix, ubigeo));
      if (rule) {
        if (rule.cutoff_hour && rule.before_cutoff && rule.after_cutoff) {
          return new Date().getHours() < rule.cutoff_hour ? rule.before_cutoff : rule.after_cutoff;
        }
        if (rule.default) return rule.default;
      }
    }
    if (method.description) return method.description;
    const zone = DELIVERY_TIMES.find((item) => matchesUbigeo(item.ubigeo_prefix, ubigeo));
    return zone ? zone.text : '';
  }

  function resolveMethodName(method, ubigeo) {
    const rules = method.display_rules && method.display_rules.rules;
    const rule = Array.isArray(rules) ? rules.find((item) => matchesUbigeo(item.ubigeo_prefix, ubigeo)) : null;
    return (rule && rule.override_name) || method.name || method.code;
  }

  // Formato de moneda de la tienda (MinimogSettings.money_format, p. ej. "S/{{amount}}"),
  // para que coincida con los precios del carrito. Recibe céntimos.
  function formatMoney(cents) {
    const format = (window.MinimogSettings && window.MinimogSettings.money_format) || 'S/{{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (number, decimals, thousands, decimal) => {
      const [integer, fraction] = number.toFixed(decimals).split('.');
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  // Los precios del proxy vienen en soles (no céntimos).
  function formatPrice(value) {
    return formatMoney(Math.round((Number(value) || 0) * 100));
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  const CART_CREATE = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl }
        userErrors { code field message }
      }
    }
  `;

  class EnovaMinicartCheckout {
    constructor(drawer) {
      this.drawer = drawer;
      this.sheet = drawer.querySelector('[data-enova-checkout-sheet]');
      this.form = this.sheet.querySelector('[data-enova-checkout-form]');
      this.modesEl = this.sheet.querySelector('[data-enova-checkout-modes]');
      this.panels = {
        delivery: this.sheet.querySelector('[data-enova-checkout-panel="delivery"]'),
        pickup: this.sheet.querySelector('[data-enova-checkout-panel="pickup"]'),
      };
      this.selects = {
        region: this.sheet.querySelector('[data-enova-ubigeo="region"]'),
        province: this.sheet.querySelector('[data-enova-ubigeo="province"]'),
        district: this.sheet.querySelector('[data-enova-ubigeo="district"]'),
      };
      this.shippingEl = this.sheet.querySelector('[data-enova-checkout-shipping]');
      this.storesEl = this.sheet.querySelector('[data-enova-checkout-stores]');
      this.pickupSummaryEl = this.sheet.querySelector('[data-enova-pickup-summary]');
      this.summaryEl = this.sheet.querySelector('[data-enova-checkout-summary]');
      this.collapses = {};
      this.sheet.querySelectorAll('[data-enova-collapse]').forEach((el) => {
        this.collapses[el.dataset.enovaCollapse] = el;
      });
      this.subtitleEl = this.sheet.querySelector('.enova-minicart-sheet__subtitle');
      this.documentEl = this.sheet.querySelector('[data-enova-checkout-document]');
      this.documentFields = {
        type: this.documentEl.querySelector('[data-enova-document="type"]'),
        number: this.documentEl.querySelector('[data-enova-document="number"]'),
        invoice: this.documentEl.querySelector('[data-enova-document="invoice"]'),
        companyField: this.documentEl.querySelector('[data-enova-document="company-field"]'),
        companyLabel: this.documentEl.querySelector('[data-enova-document="company-label"]'),
        company: this.documentEl.querySelector('[data-enova-document="company"]'),
      };
      this.errorEl = this.sheet.querySelector('[data-enova-checkout-error]');
      this.submitBtn = this.sheet.querySelector('[data-enova-checkout-submit]');
      this.submitLabel = this.sheet.querySelector('[data-enova-checkout-submit-label]');
      this.msg = this.sheet.dataset;

      this.mode = null;
      this.methods = [];
      this.stores = [];
      this.addressMapping = Object.assign({}, DEFAULT_ADDRESS_MAPPING);
      this.documents = deriveDocuments(CFG.documents) || DEFAULT_DOCUMENTS;
      this.ratesRequest = 0;
      this.initialized = false;
      // Secciones mostradas como resumen (datos guardados). Al editarlas se abren y quedan así.
      this.compact = { delivery: false, document: false };
      this.ubigeoLoaded = false;
      this.storesLoaded = false;

      this.onKeydown = this.onKeydown.bind(this);
      drawer.addEventListener('click', this.onDrawerClick.bind(this), true);
      this.sheet.addEventListener('click', this.onSheetClick.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.modesEl.addEventListener('change', (event) => {
        if (event.target.name === 'enova-minicart-mode') this.setMode(event.target.value);
      });
      this.selects.region.addEventListener('change', () => this.onRegionChange());
      this.selects.province.addEventListener('change', () => this.onProvinceChange());
      this.selects.district.addEventListener('change', () => this.onDistrictChange());
      this.shippingEl.addEventListener('change', () => {
        this.clearError();
        this.renderTotal();
      });
      this.storesEl.addEventListener('change', () => {
        this.clearError();
        this.renderPickupSummary();
        this.renderSaveLinks();
      });
      // Desde que el cliente toca el bloque, lo suyo manda sobre lo guardado.
      this.documentEl.addEventListener('input', () => (this.documentTouched = true), true);
      this.documentEl.addEventListener('change', () => (this.documentTouched = true), true);
      this.documentFields.type.addEventListener('change', () => {
        this.documentFields.type.classList.remove('has-error');
        this.documentFields.number.classList.remove('has-error');
        this.applyDocumentType();
      });
      this.documentFields.number.addEventListener('input', () => {
        this.documentFields.number.classList.remove('has-error');
        this.sanitizeField(this.documentFields.number, this.selectedDocument());
        this.renderDocumentSave();
      });
      this.documentFields.company.addEventListener('input', () => {
        this.documentFields.company.classList.remove('has-error');
        this.sanitizeField(this.documentFields.company, this.documents.company);
        this.renderDocumentSave();
      });
      this.documentFields.invoice.addEventListener('change', () => {
        this.documentFields.company.classList.remove('has-error');
        this.toggleInvoice(this.documentFields.invoice.checked, { focus: true });
      });
      this.watchHeight();

      // Al volver del checkout con «atrás» (o el gesto de swipe en iOS/Android) la página
      // se restaura desde el bfcache tal cual se dejó: con el botón en «Procesando…».
      window.addEventListener('pageshow', (event) => {
        if (event.persisted || this.submitting) this.setSubmitting(false);
      });

      // Si se cierra el drawer, la hoja se cierra con él.
      new MutationObserver(() => {
        if (!drawer.classList.contains('is-open') && this.isOpen) this.close({ restoreFocus: false });
        // Abrir el carrito (p. ej. al agregar un producto) también prepara la hoja.
        if (drawer.classList.contains('is-open')) this.prefetch();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.schedulePrefetch();
    }

    // Precarga: con el navegador libre tras cargar la página se arma la hoja en segundo plano
    // (config, ubigeo o tienda guardados, tarifa y documento), así al abrirla ya está lista.
    schedulePrefetch() {
      const run = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => this.prefetch(), { timeout: 3000 });
        else setTimeout(() => this.prefetch(), 1200);
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    }

    prefetch() {
      if (this.initialized) return;
      // Sin productos no hay a dónde ir a pagar: no se consulta nada.
      const summary = this.readDrawerSummary();
      if (!summary || !summary.count) return;
      this.cartSummary = summary;
      this.initialized = true;
      this.init();
    }

    // Firma del carrito para saber si la tarifa precargada sigue vigente.
    cartSignature(summary) {
      return summary ? `${summary.count}|${summary.total}|${summary.original}` : '';
    }

    get isOpen() {
      return this.sheet.classList.contains('is-open');
    }

    // Capa de GPU para el panel solo mientras sube/baja (se crea antes del primer frame
    // y se libera al terminar, para no retener memoria).
    markSliding() {
      clearTimeout(this.slideTimer);
      this.sheet.classList.add('is-sliding');
      this.slideTimer = setTimeout(() => this.sheet.classList.remove('is-sliding'), 500);
    }

    // Anima la altura del panel cuando cambia su contenido (paso 2, tarifa, tiendas…).
    // ResizeObserver corre tras el layout y antes del pintado: se fija la altura anterior,
    // y en el siguiente frame la nueva, así el salto nunca llega a verse.
    watchHeight() {
      const panel = this.sheet.querySelector('.enova-minicart-sheet__panel');
      const head = this.sheet.querySelector('.enova-minicart-sheet__head');
      const inner = this.sheet.querySelector('[data-enova-checkout-inner]');
      const actions = this.sheet.querySelector('[data-enova-checkout-actions]');
      if (!panel || !head || !inner || !actions || typeof ResizeObserver === 'undefined') return;

      // Altura natural = encabezado + contenido del cuerpo + acciones (sin depender del
      // alto actual del panel, que es justo lo que se anima).
      const naturalHeight = () => head.offsetHeight + inner.offsetHeight + actions.offsetHeight;

      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
      let lastHeight = 0;
      let frame = 0;
      let restTop = null;
      let glideTimer = 0;

      // El botón nunca salta: si un cambio de contenido o el fin de la animación lo mueve de
      // lugar, el pie (resumen + botón) se desliza desde donde estaba (FLIP con transform, antes
      // del pintado). La referencia es el botón: si el resumen cambia de alto, se absorbe arriba.
      const button = actions.querySelector('[data-enova-checkout-submit]') || actions;
      const buttonTop = () => button.getBoundingClientRect().top;
      const glide = (fromTop) => {
        const layoutTop = buttonTop() - (actions.enovaGlideOffset || 0);
        if (fromTop == null || (reduceMotion && reduceMotion.matches)) return layoutTop;
        const delta = fromTop - layoutTop;
        if (Math.abs(delta) < 1) return layoutTop;
        actions.enovaGlideOffset = delta;
        actions.style.transition = 'none';
        actions.style.transform = `translate3d(0, ${delta}px, 0)`;
        requestAnimationFrame(() => {
          actions.enovaGlideOffset = 0;
          actions.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          actions.style.transform = '';
        });
        clearTimeout(glideTimer);
        glideTimer = setTimeout(() => (actions.style.transition = ''), 340);
        return layoutTop;
      };

      const finish = (event) => {
        if (event && (event.target !== panel || event.propertyName !== 'height')) return;
        const visibleTop = buttonTop();
        panel.style.height = '';
        panel.classList.remove('is-resizing', 'is-pinned');
        panel.style.removeProperty('will-change');
        restTop = this.isOpen ? glide(visibleTop) : null;
      };
      panel.addEventListener('transitionend', finish);

      const observer = new ResizeObserver(() => {
        const height = naturalHeight();
        const previous = lastHeight;
        lastHeight = height;
        if (!previous || previous === height || !this.isOpen || (reduceMotion && reduceMotion.matches)) {
          restTop = this.isOpen ? buttonTop() : null;
          return;
        }
        // Un bloque que se despliega solo (p. ej. el RUC) ya anima su alto: la hoja lo sigue tal cual.
        if (performance.now() < this.heightFrozenUntil) {
          cancelAnimationFrame(frame);
          finish();
          return;
        }

        cancelAnimationFrame(frame);
        // Si ya había una animación en curso, parte desde la altura visible actual.
        const from = panel.style.height ? panel.getBoundingClientRect().height : previous;
        // Durante la animación el botón se mantiene abajo; al terminar, si su lugar en reposo es
        // otro (contenido más alto que la pantalla), se desliza hasta ahí.
        panel.classList.add('is-resizing', 'is-pinned');
        panel.style.willChange = this.sheet.classList.contains('is-sliding') ? 'transform, height' : 'height';
        panel.style.height = `${from}px`;
        restTop = glide(restTop);
        frame = requestAnimationFrame(() => {
          panel.style.height = `${height}px`;
          // Si la altura queda topada por max-height no hay transitionend: se limpia igual.
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(finish, 450);
        });
      });
      [head, inner, actions].forEach((node) => observer.observe(node));
    }

    // Pausa la animación de alto de la hoja mientras un bloque anima el suyo.
    freezeHeightAnimation(duration) {
      this.heightFrozenUntil = performance.now() + duration;
    }

    onDrawerClick(event) {
      const trigger = event.target.closest('[data-enova-minicart-checkout]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.trigger = trigger;
      this.open();
    }

    onSheetClick(event) {
      const save = event.target.closest('[data-enova-save]');
      if (save) {
        event.preventDefault();
        if (save.dataset.enovaSave === 'document') this.saveDocument();
        else this.saveDelivery();
        return;
      }
      const summaryRow = event.target.closest('[data-enova-summary]');
      if (summaryRow) {
        event.preventDefault();
        this.expand(summaryRow.dataset.enovaSummary, { focus: true });
        return;
      }
      if (event.target.closest('[data-enova-checkout-switch-delivery]')) {
        event.preventDefault();
        this.setMode('delivery');
        return;
      }
      if (event.target.closest('[data-enova-checkout-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    open() {
      this.sheet.removeAttribute('inert');
      this.sheet.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('has-checkout-sheet');
      this.markSliding();
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      document.addEventListener('keydown', this.onKeydown, true);

      this.clearError();
      this.setSubmitting(false);
      // Montos del carrito al instante desde el pie del mini-cart (se re-renderiza con cada
      // cambio): así el resumen nace completo y no le aparecen líneas después.
      const drawerSummary = this.readDrawerSummary();
      if (drawerSummary) this.cartSummary = drawerSummary;
      if (!this.initialized) {
        this.initialized = true;
        this.init();
      }
      // El carrito pudo cambiar: se recalcula la tarifa o se revalida el stock al reabrir.
      if (this.mode === 'delivery') {
        // Si la tarifa ya se calculó (o se está calculando) para este distrito y este mismo
        // carrito, se reutiliza: la hoja abre con el resumen completo, sin skeleton.
        const district = this.selectedOption('district');
        const sameDistrict = !!district && district.dataset.code === this.ratesUbigeo;
        const fresh =
          sameDistrict &&
          (this.ratesInFlight || (this.methods.length && this.ratesCartSig === this.cartSignature(drawerSummary)));
        if (!fresh) this.onDistrictChange();
        if (this.shippingPlaceholder) this.refreshCartSummary();
      }
      if (this.mode === 'pickup') this.renderPickupSummary();
      if (this.mode === 'pickup' && this.storesLoaded) this.loadStores();

      setTimeout(() => {
        const target = this.compact.delivery
          ? this.submitBtn
          : this.mode
            ? this.form.querySelector('input:checked, select:not(:disabled)')
            : this.modesEl.querySelector('input');
        if (target) target.focus({ preventScroll: true });
      }, 320);
    }

    close({ restoreFocus = true } = {}) {
      this.markSliding();
      this.sheet.classList.remove('is-open');
      this.sheet.setAttribute('aria-hidden', 'true');
      this.sheet.setAttribute('inert', '');
      this.drawer.classList.remove('has-checkout-sheet');
      document.removeEventListener('keydown', this.onKeydown, true);
      if (!restoreFocus) return;
      const trigger =
        this.trigger && document.contains(this.trigger)
          ? this.trigger
          : this.drawer.querySelector('[data-enova-minicart-checkout]');
      if (trigger) trigger.focus({ preventScroll: true });
    }

    init() {
      this.saved = readCustomerData();
      this.restoreDocument();

      // Config del backend (?scope=settings): mapeo de dirección, qué modos están activos y
      // los tipos de documento con su largo.
      getProxyJSON('scope=settings', { cache: true })
        .then((settings) => {
          if (!settings || typeof settings !== 'object') return;
          if (settings.addressMapping) {
            this.addressMapping = Object.assign({}, DEFAULT_ADDRESS_MAPPING, settings.addressMapping);
          }
          const documents = deriveDocuments(settings.documents);
          if (documents) {
            this.documents = documents;
            this.renderDocumentTypes();
            // Si lo guardado ya no cumple con los tipos del backend, se pide de nuevo.
            if (this.compact.document && !this.readDocument({ mark: false })) this.expand('document');
            else this.renderSummary();
          }
          if (settings.dispatch) this.applyDispatchConfig(settings.dispatch);
        })
        .catch(() => {});

      const savedMode =
        this.saved.dispatch_mode === 'recojo-tienda' || this.saved.dispatch_mode === 'pickup'
          ? 'pickup'
          : this.saved.dispatch_mode === 'delivery'
            ? 'delivery'
            : null;
      // Quien ya tiene datos guardados ve un resumen en vez del formulario completo.
      const saved = this.saved;
      this.compact.delivery =
        savedMode === 'delivery'
          ? !!(saved.region && saved.province && saved.district)
          : savedMode === 'pickup' && !!saved.pickup_store;
      this.compact.document = this.compact.delivery && !!this.readDocument({ mark: false });
      if (savedMode) this.setMode(savedMode);
      this.applyCompact();
    }

    /* ---------------- Resumen compacto ---------------- */

    // Abre o cierra un plegable. Con la hoja abierta se anima (y la hoja sigue su alto cuadro
    // a cuadro); cerrada o al restaurar, cambia directo.
    setCollapse(el, open, animate) {
      if (!el || el.classList.contains('is-open') === open) return;
      if (!animate) {
        el.classList.add('is-instant');
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-instant')));
      }
      el.classList.toggle('is-open', open);
      el.inert = !open;
      el.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    canAnimate() {
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return this.isOpen && !reduceMotion;
    }

    // Filas del resumen ↔ formulario: lo resumido se pliega y su fila se despliega a la vez.
    applyCompact() {
      const { delivery, document } = this.compact;
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(380);
      const states = {
        review: delivery || document,
        'summary-delivery': delivery,
        'summary-document': document,
        modes: !delivery && !this.modesHiddenByConfig,
        ubigeo: !delivery,
        stores: !delivery,
        document: !document,
      };
      Object.keys(states).forEach((key) => this.setCollapse(this.collapses[key], states[key], animate));
      if (this.subtitleEl) {
        this.subtitleEl.textContent = delivery ? this.msg.msgSubtitleReview : this.msg.msgSubtitle;
      }
      this.renderSummary();
    }

    // Abre una sección del resumen para editarla (o porque lo guardado ya no sirve).
    expand(section, { focus = false } = {}) {
      if (!this.compact[section]) return;
      this.compact[section] = false;
      this.applyCompact();
      if (!focus) return;
      const target =
        section === 'document'
          ? this.documentFields.number
          : this.modesEl.querySelector('input:checked') || this.modesEl.querySelector('input');
      // Foco al terminar de abrirse. En pantallas táctiles no se fuerza el teclado.
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!target || (coarse && section === 'document')) return;
      setTimeout(() => target.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    collapse(section) {
      if (this.compact[section]) return;
      this.compact[section] = true;
      this.applyCompact();
    }

    // «Guardar selección» aparece bajo el distrito (o las tiendas) cuando la elección está
    // completa; al tocarlo, esa parte se pliega en la fila «Entrega».
    renderSaveLinks() {
      const district = this.selectedOption('district');
      const store = this.selectedStore();
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.collapses['save-delivery'], !!(district && district.dataset.code), animate);
      this.setCollapse(this.collapses['save-pickup'], !!(store && store.availability.selectable), animate);
    }

    saveDelivery() {
      this.collapse('delivery');
      // El foco pasa a «Cambiar» de la fila recién desplegada (el enlace quedó plegado).
      const row = this.summaryEl.querySelector('[data-enova-summary="delivery"]');
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    // «Guardar» dentro del campo: sin factura, en el del documento cuando está completo; con
    // factura, en el del RUC cuando documento y RUC están completos.
    renderDocumentSave() {
      const doc = this.selectedDocument();
      const number = this.documentFields.number.value.trim();
      const needsInvoice = this.documentFields.invoice.checked;
      const company = this.documents.company || { minLen: 1, maxLen: 20, numeric: false };
      const numberOk = isDocumentValid(number, doc);
      const rucOk = isDocumentValid(this.documentFields.company.value.trim(), company);
      this.toggleSave('number', numberOk && !needsInvoice);
      this.toggleSave('company', numberOk && needsInvoice && rucOk);
    }

    toggleSave(field, visible) {
      const wrap = this.documentEl.querySelector(`[data-enova-save-wrap="${field}"]`);
      if (!wrap) return;
      const button = wrap.querySelector('[data-enova-save]');
      wrap.classList.toggle('has-save', visible);
      button.tabIndex = visible ? 0 : -1;
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    saveDocument() {
      if (!this.readDocument()) {
        this.showError(this.documentMessage);
        return;
      }
      this.collapse('document');
      const row = this.summaryEl.querySelector('[data-enova-summary="document"]');
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    setSummaryRow(section, title, detail) {
      const row = this.summaryEl.querySelector(`[data-enova-summary="${section}"]`);
      row.querySelector('[data-enova-summary-title]').textContent = title;
      row.querySelector('[data-enova-summary-detail]').textContent = detail;
      row.setAttribute('aria-label', `${title}. ${detail}. ${row.querySelector('.enova-minicart-review__edit').textContent}`);
    }

    renderSummary() {
      if (this.compact.delivery && this.mode) {
        const modeTitle = this.modesEl.querySelector(`[data-enova-checkout-mode="${this.mode}"] .enova-minicart-mode__title`);
        let detail;
        if (this.mode === 'pickup') {
          const store = this.selectedStore();
          detail = store ? [store.name, store.address].filter(Boolean).join(' · ') : this.saved.pickup_store || '';
        } else {
          const pick = (level) => (this.selectedOption(level) || {}).value || this.saved[level] || '';
          detail = [pick('district'), pick('province'), pick('region')].filter(Boolean).join(', ');
        }
        this.setSummaryRow('delivery', modeTitle ? modeTitle.textContent.trim() : '', detail);
      }
      if (this.compact.document) {
        const doc = this.selectedDocument();
        const person = `${doc.label} ${this.documentFields.number.value.trim()}`;
        const invoice = this.documentFields.invoice.checked;
        const company = this.documents.company ? this.documents.company.label : 'RUC';
        this.setSummaryRow(
          'document',
          invoice ? this.msg.msgReceiptFactura : this.msg.msgReceiptBoleta,
          invoice ? `${company} ${this.documentFields.company.value.trim()} · ${person}` : person
        );
      }
    }

    applyDispatchConfig(dispatch) {
      const pickup = dispatch.pickup;
      const enabled = {
        delivery: dispatch.delivery !== false,
        pickup: pickup && typeof pickup === 'object' ? pickup.enabled !== false : pickup !== false,
      };
      Object.keys(enabled).forEach((mode) => {
        const option = this.modesEl.querySelector(`[data-enova-checkout-mode="${mode}"]`);
        if (option) option.hidden = !enabled[mode];
      });

      const available = Object.keys(enabled).filter((mode) => enabled[mode]);
      // Con una sola opción no hay nada que elegir: se selecciona y se oculta el paso 1.
      this.modesEl.hidden = available.length === 1;
      this.modesHiddenByConfig = available.length === 1;
      this.applyCompact();
      if (available.length === 1) this.setMode(available[0]);
      else if (this.mode && !enabled[this.mode]) this.setMode(null);
    }

    setMode(mode) {
      const previous = this.mode;
      this.mode = mode;
      // El resumen describe el modo guardado: si la config obliga a otro, se muestra el formulario.
      if (!mode || (previous && previous !== mode)) this.expand('delivery');
      this.clearError();
      this.modesEl.querySelectorAll('input[name="enova-minicart-mode"]').forEach((input) => {
        input.checked = input.value === mode;
        input.closest('.enova-minicart-mode').classList.toggle('is-active', input.value === mode);
      });
      // Alto del panel que sale: el que entra lo conserva mientras carga su contenido, así la
      // hoja no baja para luego volver a subir (una sola transición, al alto real).
      const outgoing = previous && previous !== mode ? this.panels[previous] : null;
      const heldHeight = outgoing && !outgoing.hidden && this.isOpen ? outgoing.offsetHeight : 0;
      // El documento y el alto conservado se aplican recién al intercambiar los paneles: así el
      // documento se desvanece con el panel que sale y aparece con el que entra.
      this.switchPanels(mode ? this.panels[mode] : null, () => {
        this.placeDocument(mode);
        this.showSummaryFor(mode);
        if (mode === 'pickup' && this.storesPending && !this.stores.length && heldHeight) {
          this.panels.pickup.style.minHeight = `${heldHeight}px`;
        }
      });
      this.form.classList.toggle('has-mode', !!mode);
      if (mode === 'pickup') this.renderPickupSummary();
      // En envío a domicilio el resumen siempre está: si aún no hay tarifa, en skeleton.
      if (mode === 'delivery' && (this.shippingEl.hidden || this.shippingPlaceholder)) {
        this.markShippingUpdating();
        this.refreshCartSummary();
      }

      if (mode === 'delivery' && !this.ubigeoLoaded) {
        this.ubigeoLoaded = true;
        this.loadOptions('region', `scope=departaments&country=${COUNTRY_PROXY_ID}`, this.saved.region).then((restored) => {
          if (restored) this.onRegionChange(this.saved);
          else this.expand('delivery');
        });
      }
      if (mode === 'pickup' && !this.storesLoaded) {
        this.storesLoaded = true;
        this.loadStores();
      }
    }

    releasePanelHeight(panel) {
      if (panel) panel.style.minHeight = '';
    }

    // Transición «fade through» entre paneles: primero se desvanece el que sale (rápido, sin
    // moverse de su lugar), luego se intercambia el contenido —la altura se ajusta una sola
    // vez— y entra el nuevo con un fundido suave. Nunca se ven los dos superpuestos.
    switchPanels(incoming, swap) {
      clearTimeout(this.panelTimer);
      const leaving = Object.values(this.panels).filter((panel) => panel !== incoming && !panel.hidden);
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const finish = () => {
        leaving.forEach((panel) => {
          panel.hidden = true;
          panel.classList.remove('is-leaving');
          this.releasePanelHeight(panel);
        });
        swap();
        if (!incoming) return;
        const wasHidden = incoming.hidden;
        incoming.classList.remove('is-leaving');
        if (wasHidden) this.enterPanel(incoming);
      };

      if (!leaving.length || !this.isOpen || reduceMotion) {
        finish();
        return;
      }
      leaving.forEach((panel) => panel.classList.add('is-leaving'));
      this.sheet.querySelectorAll('[data-enova-summary-for]:not([hidden])').forEach((el) => el.classList.add('is-leaving'));
      this.panelTimer = setTimeout(finish, 150);
    }

    enterPanel(panel) {
      panel.hidden = false;
    }

    // Resumen de montos del modo activo (en el pie, junto a «Ir a pagar»).
    showSummaryFor(mode) {
      this.sheet.querySelectorAll('[data-enova-summary-for]').forEach((el) => {
        const active = el.dataset.enovaSummaryFor === mode;
        const wasHidden = el.hidden;
        el.classList.remove('is-leaving');
        el.hidden = !active;
        if (active && wasHidden) this.flash(el, 'is-entering', 280);
      });
    }

    // Animación puntual (valores recién calculados, tiendas que llegan, campo RUC): la clase se
    // pone solo si el elemento está a la vista y se retira al terminar. Si quedara puesta, el
    // navegador la volvería a correr cada vez que el panel reaparece (el parpadeo del final).
    flash(el, className, duration = 320) {
      clearTimeout(el.enovaFlashTimer);
      el.classList.remove(className);
      if (!this.isOpen || el.closest('[hidden]')) return;
      void el.offsetWidth;
      el.classList.add(className);
      el.enovaFlashTimer = setTimeout(() => el.classList.remove(className), duration);
    }

    /* ---------------- Documento y comprobante ---------------- */

    // Un solo bloque para ambos modos: se mueve al panel activo. En envío va antes del
    // resumen, para que el total quede junto al botón; en retiro, tras las tiendas.
    placeDocument(mode) {
      const panel = mode && this.panels[mode];
      this.documentEl.hidden = !panel;
      if (!panel) return;
      panel.appendChild(this.documentEl);
    }

    // Arma los tipos (respaldo al abrir, luego los del backend). Mientras el cliente no toque
    // el bloque se reaplica lo guardado: así un tipo que solo trae el backend (p. ej.
    // pasaporte) se restaura con su número intacto aunque la config llegue después.
    renderDocumentTypes() {
      const select = this.documentFields.type;
      const saved = this.saved || {};
      const preferred = this.documentTouched ? select.value : saved.document_type || select.value;
      select.innerHTML = this.documents.person
        .map((doc) => `<option value="${escapeHTML(doc.value)}">${escapeHTML(doc.label)}</option>`)
        .join('');
      if (this.documents.person.some((doc) => doc.value === preferred)) select.value = preferred;
      if (!this.documentTouched) {
        this.documentFields.number.value = saved.document_number || '';
        this.documentFields.company.value = saved.company_document || '';
      }
      this.applyDocumentType();

      const company = this.documents.company;
      if (company) {
        this.documentFields.companyLabel.textContent = company.label;
        this.documentFields.company.maxLength = company.maxLen;
        this.documentFields.company.inputMode = company.numeric === false ? 'text' : 'numeric';
      }
      this.sanitizeField(this.documentFields.company, company);
    }

    selectedDocument() {
      const value = this.documentFields.type.value;
      return this.documents.person.find((doc) => doc.value === value) || this.documents.person[0];
    }

    // Largo y teclado del número según el tipo. Al cambiar de tipo se conserva lo escrito
    // (solo se recorta lo que el nuevo tipo no admite).
    applyDocumentType() {
      const doc = this.selectedDocument();
      const input = this.documentFields.number;
      input.maxLength = doc.maxLen;
      input.inputMode = doc.numeric === false ? 'text' : 'numeric';
      this.sanitizeField(input, doc);
      this.renderDocumentSave();
    }

    sanitizeField(input, doc) {
      const clean = sanitizeDocument(input.value, doc);
      if (clean !== input.value) input.value = clean;
    }

    toggleInvoice(needsInvoice, { focus = false } = {}) {
      this.documentFields.invoice.checked = needsInvoice;
      // Solo se anima cuando lo hace el cliente con la hoja abierta; al restaurar datos, directo.
      // Mientras se despliega, la hoja sigue al contenido cuadro a cuadro: el resumen no salta.
      const animate = focus && this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.documentFields.companyField, needsInvoice, animate);
      this.renderDocumentSave();
      // Foco en el RUC al terminar de desplegarse. En pantallas táctiles no se fuerza: abrir el
      // teclado a mitad de la animación mueve la vista; el cliente toca el campo cuando quiera.
      clearTimeout(this.invoiceFocusTimer);
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (needsInvoice && focus && !coarse) {
        this.invoiceFocusTimer = setTimeout(
          () => this.documentFields.company.focus({ preventScroll: true }),
          animate ? 300 : 0
        );
      }
    }

    // Mismas claves de customerData que /cart: lo escrito en un lado aparece en el otro.
    restoreDocument() {
      this.renderDocumentTypes();
      this.toggleInvoice(this.saved.receipt_type === 'factura');
    }

    documentError(doc, value) {
      if (!value) return String(this.msg.msgDocumentRequired || '').replace('__LABEL__', doc.label);
      const length =
        doc.minLen === doc.maxLen
          ? String(doc.minLen)
          : String(this.msg.msgDocumentRange || '').replace('__MIN__', doc.minLen).replace('__MAX__', doc.maxLen);
      const template = doc.numeric === false ? this.msg.msgDocumentAlphanumeric : this.msg.msgDocumentNumeric;
      return String(template || '').replace('__LABEL__', doc.label).replace('__LENGTH__', length);
    }

    // Misma validación que /cart (largo y caracteres por tipo). Devuelve los note_attributes
    // del documento o null; en error marca el campo y deja el mensaje en `this.documentMessage`.
    readDocument({ mark = true } = {}) {
      const doc = this.selectedDocument();
      const number = this.documentFields.number.value.trim();
      const needsInvoice = this.documentFields.invoice.checked;
      const company = this.documents.company || { label: 'RUC', minLen: 1, maxLen: 20, numeric: false };
      const ruc = this.documentFields.company.value.trim();
      this.documentMessage = '';
      this.invalidDocumentField = null;
      if (mark) {
        this.documentFields.number.classList.remove('has-error');
        this.documentFields.company.classList.remove('has-error');
      }
      const invalid = [];

      if (!isDocumentValid(number, doc)) {
        invalid.push(this.documentFields.number);
        this.documentMessage = this.documentError(doc, number);
        this.invalidDocumentField = this.documentFields.number;
      }
      if (needsInvoice && !isDocumentValid(ruc, company)) {
        invalid.push(this.documentFields.company);
        if (!this.documentMessage) {
          this.documentMessage = this.documentError(company, ruc);
          this.invalidDocumentField = this.documentFields.company;
        }
      }
      if (mark) invalid.forEach((input) => input.classList.add('has-error'));
      if (this.documentMessage) return null;

      const attributes = {
        document_type: doc.value,
        document_number: number,
        receipt_type: needsInvoice ? 'factura' : 'boleta',
      };
      if (needsInvoice) attributes.company_document = ruc;
      return {
        attributes,
        saved: Object.assign({}, attributes, { company_document: ruc }),
      };
    }

    /* ---------------- Envío a domicilio ---------------- */

    // Llena un select desde el proxy. Devuelve true si pudo restaurar `savedValue`.
    loadOptions(level, query, savedValue) {
      const select = this.selects[level];
      select.disabled = true;
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgLoading)}</option>`;

      return getProxyJSON(query, { cache: true })
        .then((list) => {
          const items = (Array.isArray(list) ? list : []).filter((item) => !('is_active' in item) || item.is_active);
          let options = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          items.forEach((item) => {
            options += `<option value="${escapeHTML(item.name)}" data-id="${escapeHTML(item.id)}" data-code="${escapeHTML(item.code)}">${escapeHTML(item.name)}</option>`;
          });
          select.innerHTML = options;
          select.disabled = false;

          if (savedValue && items.some((item) => item.name === savedValue)) {
            select.value = savedValue;
            return true;
          }
          return false;
        })
        .catch(() => {
          select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          select.disabled = false;
          return false;
        });
    }

    resetSelect(level) {
      const select = this.selects[level];
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
      select.disabled = true;
      if (level === 'district') this.renderSaveLinks();
    }

    selectedOption(level) {
      const select = this.selects[level];
      return select.value ? select.options[select.selectedIndex] : null;
    }

    onRegionChange(saved = {}) {
      this.clearError();
      this.resetSelect('province');
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      if (!region) return;

      const restoreProvince = saved.region === region.value ? saved.province : null;
      this.loadOptions(
        'province',
        `scope=provinces&country=${COUNTRY_PROXY_ID}&departament=${region.dataset.id}`,
        restoreProvince
      ).then((restored) => {
        if (restored) this.onProvinceChange(saved);
        else if (saved.province) this.expand('delivery');
      });
    }

    onProvinceChange(saved = {}) {
      this.clearError();
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      if (!region || !province) return;

      const restoreDistrict = saved.province === province.value ? saved.district : null;
      this.loadOptions(
        'district',
        `scope=distrits&country=${COUNTRY_PROXY_ID}&departament=${region.dataset.id}&province=${province.dataset.id}`,
        restoreDistrict
      ).then((restored) => {
        if (restored) this.onDistrictChange(saved);
        else if (saved.district) this.expand('delivery');
      });
    }

    onDistrictChange(saved = {}) {
      this.clearError();
      this.renderSaveLinks();
      const district = this.selectedOption('district');
      if (!district || !district.dataset.code) {
        this.resetShipping();
        return;
      }
      const current = this.selectedMethod();
      this.fetchRates(district.dataset.code, saved.shipping_method || (current && current.code));
    }

    // ¿Ya hay un resumen con total a la vista? Entonces se conserva al recalcular.
    hasShippingSummary() {
      return !this.shippingEl.hidden && !!this.shippingEl.querySelector('[data-enova-checkout-total]');
    }

    // Departamento o provincia cambió (o se vació el distrito): el resumen se queda con los
    // valores que van a cambiar en skeleton en vez de desaparecer.
    resetShipping() {
      this.ratesRequest += 1;
      this.ratesInFlight = false;
      this.ratesUbigeo = null;
      this.methods = [];
      this.shippingEl.classList.remove('is-loading');
      this.markShippingUpdating();
    }

    // UI optimista: el resumen de envío a domicilio siempre está a la vista. Mientras no hay
    // tarifa, el envío y el total llevan un skeleton sutil; si todavía no hubo ninguna, se pinta
    // la misma estructura con los productos y descuentos reales del carrito.
    markShippingUpdating() {
      this.shippingEl.hidden = false;
      this.shippingEl.setAttribute('aria-busy', 'true');
      this.shippingEl.classList.remove('is-refreshed');
      this.shippingEl.classList.add('is-updating');
      if (!this.hasShippingSummary() || this.shippingPlaceholder) this.renderShippingPlaceholder();
    }

    // { count, original, discount, total } del pie del mini-cart (en céntimos), o null.
    readDrawerSummary() {
      const el = this.drawer.querySelector('[data-enova-cart-total]');
      if (!el) return null;
      const read = (key) => Number(el.dataset[key]) || 0;
      return {
        count: read('enovaCartCount'),
        original: read('enovaCartOriginal'),
        discount: read('enovaCartDiscount'),
        total: read('enovaCartTotal'),
      };
    }

    renderShippingPlaceholder() {
      this.shippingPlaceholder = true;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const products = this.cartSummary
        ? this.renderSummaryLines()
        : `<div class="enova-minicart-sheet__line">${bar(72)}${bar(44)}</div>`;
      this.shippingEl.innerHTML = `
        <span class="enova-minicart-sr">${escapeHTML(this.msg.msgCalculating)}</span>
        ${products}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgShipping)}</span>
            <span class="enova-minicart-sheet__method-desc" style="width:112px">&nbsp;</span>
          </span>
          <span class="enova-minicart-sheet__method-price" style="width:56px">&nbsp;</span>
        </div>
        <div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value" style="width:72px" data-enova-checkout-total>&nbsp;</span>
        </div>`;
    }

    // Productos y descuentos del carrito para el resumen, aunque aún no haya distrito.
    refreshCartSummary() {
      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          const previous = JSON.stringify(this.cartSummary);
          this.cartSummary = {
            count: Number(cart.item_count) || 0,
            original: Number(cart.original_total_price) || 0,
            discount: Number(cart.total_discount) || 0,
            total: Number(cart.total_price) || 0,
          };
          // Solo se repinta si el carrito cambió: nada se mueve si los montos son los mismos.
          const changed = previous !== JSON.stringify(this.cartSummary);
          if (changed && this.shippingPlaceholder && this.mode === 'delivery') this.renderShippingPlaceholder();
          if (changed && this.mode === 'pickup') this.renderPickupSummary();
        })
        .catch(() => {});
    }

    // Tarifa del distrito: mismo scope y payload que calculateShippingMethodByUbigeoSelected().
    fetchRates(ubigeo, savedMethod) {
      const request = ++this.ratesRequest;
      this.methods = [];
      this.ratesUbigeo = ubigeo;
      this.ratesInFlight = true;
      this.shippingEl.classList.add('is-loading');
      this.markShippingUpdating();

      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          this.cartSummary = {
            count: Number(cart.item_count) || 0,
            original: Number(cart.original_total_price) || 0,
            discount: Number(cart.total_discount) || 0,
            total: Number(cart.total_price) || 0,
          };
          this.ratesCartSig = this.cartSignature(this.cartSummary);
          let totalGrams = 0;
          const products = (cart.items || []).map((item) => {
            const grams = Number(item.grams) || 0;
            totalGrams += grams * item.quantity;
            return { sku: item.sku || '', quantity: item.quantity, volumen_unit: null, weight_unit: grams, position: null };
          });
          const query = [
            'scope=district-calc-rates',
            `ubigeo=${encodeURIComponent(ubigeo)}`,
            `products=${encodeURIComponent(JSON.stringify(products))}`,
            `weight=${totalGrams / 1000}`,
          ].join('&');
          return getJSON(proxyUrl(query));
        })
        .then((methods) => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          // Los métodos con fecha/horario (envío programado) necesitan esos campos extra: se
          // eligen en /cart. Aquí solo se ofrecen los que no requieren scheduling.
          this.methods = (Array.isArray(methods) ? methods : [])
            .filter((method) => !(method.scheduling && Array.isArray(method.scheduling.days) && method.scheduling.days.length))
            .map((method) =>
              Object.assign({}, method, {
                displayName: resolveMethodName(method, ubigeo),
                effectiveDescription: resolveMethodDescription(method, ubigeo),
              })
            );
          this.renderMethods(savedMethod);
        })
        .catch(() => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          this.methods = [];
          this.renderMethods();
        });
    }

    renderMethods(savedMethod) {
      const wasUpdating = this.shippingEl.classList.contains('is-updating');
      this.shippingPlaceholder = false;
      this.shippingEl.classList.remove('is-loading', 'is-updating');
      this.shippingEl.removeAttribute('aria-busy');
      // Los valores nuevos entran con un fundido corto, sin mover el resto del resumen.
      if (wasUpdating) this.flash(this.shippingEl, 'is-refreshed');
      if (!this.methods.length) {
        this.shippingEl.innerHTML = `<span class="enova-minicart-sheet__shipping-empty">${escapeHTML(this.msg.msgUnavailable)}</span>`;
        this.expand('delivery');
        return;
      }

      const selected = this.methods.some((method) => method.code === savedMethod) ? savedMethod : this.methods[0].code;
      const single = this.methods.length === 1;
      this.shippingEl.innerHTML = this.renderSummaryLines() + this.methods
        .map((method) => {
          const price = method.is_free_shipping ? this.msg.msgFree : formatPrice(method.price);
          const description = method.effectiveDescription
            ? `<span class="enova-minicart-sheet__method-desc">${escapeHTML(method.effectiveDescription)}</span>`
            : '';
          return `
            <label class="enova-minicart-sheet__method${single ? ' is-single' : ''}">
              <input type="radio" name="enova-minicart-method" value="${escapeHTML(method.code)}"${method.code === selected ? ' checked' : ''}${single ? ' hidden' : ''}>
              <span class="enova-minicart-sheet__method-text">
                <span class="enova-minicart-sheet__method-name">${escapeHTML(method.displayName)}</span>
                ${description}
              </span>
              <span class="enova-minicart-sheet__method-price">${escapeHTML(price)}</span>
            </label>`;
        })
        .join('');
      this.shippingEl.insertAdjacentHTML(
        'beforeend',
        `<div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value" data-enova-checkout-total></span>
        </div>`
      );
      this.renderTotal();
    }

    // «Productos (n)» a precio original y, si hay cupón o descuento, la línea de descuento:
    // así Productos − Descuento + Envío = Total.
    renderSummaryLines() {
      const summary = this.cartSummary;
      if (!summary) return '';
      // Referencia con precios de comparación: la calcula el Liquid del pie del mini-cart
      // (cart.js no expone compare_at_price). Sin ella, cae al precio original del carrito.
      const referenceEl = this.drawer.querySelector('[data-enova-reference-total]');
      const liquidReference = referenceEl ? Number(referenceEl.dataset.enovaReferenceTotal) : 0;
      const reference = Math.max(liquidReference || 0, summary.original, summary.total);
      const savings = reference - summary.total;
      const label = String(this.msg.msgProducts || '').replace('__COUNT__', summary.count);
      let html = `
        <div class="enova-minicart-sheet__line">
          <span>${escapeHTML(label)}</span>
          <span>${escapeHTML(formatMoney(reference))}</span>
        </div>`;
      if (savings > 0) {
        html += `
          <div class="enova-minicart-sheet__line enova-minicart-sheet__line--discount">
            <span>${escapeHTML(this.msg.msgDiscount)}</span>
            <span>−${escapeHTML(formatMoney(savings))}</span>
          </div>`;
      }
      return html;
    }

    // Total = total del carrito (con descuentos) + envío elegido. Solo en envío a domicilio.
    renderTotal() {
      const totalEl = this.shippingEl.querySelector('[data-enova-checkout-total]');
      const method = this.selectedMethod();
      if (!totalEl || !method) return;
      const shipping = method.is_free_shipping ? 0 : Math.round((Number(method.price) || 0) * 100);
      const cartTotal = this.cartSummary ? this.cartSummary.total : 0;
      totalEl.textContent = formatMoney(cartTotal + shipping);
    }

    selectedMethod() {
      const input = this.shippingEl.querySelector('input[name="enova-minicart-method"]:checked');
      if (!input) return null;
      return this.methods.find((method) => method.code === input.value) || null;
    }

    /* ---------------- Retiro en tienda ---------------- */

    // Tiendas de retiro (shop-addresses) + stock por sucursal del carrito (scope=stock, que
    // además trae el ID real de cada location de Shopify). Se revalida al reabrir la hoja.
    loadStores() {
      const request = (this.storesRequest = (this.storesRequest || 0) + 1);
      this.storesPending = true;
      if (!this.stores.length) this.storesEl.innerHTML = this.renderStoresSkeleton();

      return Promise.all([getProxyJSON('scope=shop-addresses', { cache: true }), this.fetchPickupStock()])
        .then(([list, stock]) => {
          if (request !== this.storesRequest) return;
          this.storesPending = false;
          const mapping = CFG.pickupLocations || {};
          this.stores = (Array.isArray(list) ? list : [])
            .slice()
            .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
            .map((store) => {
              const location = stock.locations[normalizeName(store.name)] || null;
              const configured = mapping[store.code] || mapping[store.name];
              return Object.assign({}, store, {
                locationId: toLocationId(configured || (location && location.id) || ''),
                availability: this.storeAvailability(location, stock),
              });
            });
          this.renderStores();
        })
        .catch(() => {
          if (request !== this.storesRequest) return;
          this.storesPending = false;
          this.storesLoaded = false;
          this.stores = [];
          this.storesEl.innerHTML = `<p class="enova-minicart-stores__status">${escapeHTML(this.msg.msgStoresError)}</p>`;
          this.releasePanelHeight(this.panels.pickup);
          if (this.mode === 'pickup') this.expand('delivery');
        });
    }

    // Stock del carrito por sucursal: { items: [{variantId, quantity}], locations: {nombre: {id, stock}} }.
    // Si falla, devuelve ok=false y las tiendas quedan elegibles (fail-open, como /cart).
    fetchPickupStock() {
      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          const quantities = {};
          (cart.items || []).forEach((item) => {
            quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity;
          });
          const items = Object.keys(quantities).map((variantId) => ({ variantId, quantity: quantities[variantId] }));
          if (!items.length) return { ok: false, items, locations: {} };

          return getJSON(proxyUrl(`scope=stock&variants=${items.map((item) => item.variantId).join(',')}`)).then((list) => {
            const locations = {};
            (Array.isArray(list) ? list : []).forEach((entry) => {
              const variantId = String(entry.id || '').split('/').pop();
              (entry.locations || []).forEach((location) => {
                const key = normalizeName(location.name);
                if (!locations[key]) locations[key] = { id: location.id, stock: {} };
                locations[key].stock[variantId] = Number(location.available) || 0;
              });
            });
            return { ok: true, items, locations };
          });
        })
        .catch(() => ({ ok: false, items: [], locations: {} }));
    }

    // { selectable, missing }: cuántos productos del carrito no alcanzan en esa sucursal.
    storeAvailability(location, stock) {
      if (!PICKUP_STOCK_VALIDATION || !stock.ok || !location) return { selectable: true, missing: 0, known: false };
      const missing = stock.items.filter((item) => (location.stock[item.variantId] || 0) < item.quantity).length;
      return { selectable: missing === 0, missing, known: true, total: stock.items.length };
    }

    // Costo de retiro de una tienda, en céntimos (0 = gratis).
    storePrice(store) {
      return store.price_enabled && Number(store.price) > 0 ? Math.round(Number(store.price) * 100) : 0;
    }

    // Resumen de retiro, igual que el de envío: productos, descuentos, retiro y total. Si todas
    // las tiendas cuestan lo mismo, el total se conoce antes de elegir; si no, o mientras
    // cargan, el costo y el total quedan en skeleton.
    renderPickupSummary() {
      const el = this.pickupSummaryEl;
      if (!el) return;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const store = this.selectedStore();
      const selectable = this.stores.filter((item) => item.availability.selectable);
      const prices = [...new Set(selectable.map((item) => this.storePrice(item)))];
      const price = store ? this.storePrice(store) : prices.length === 1 ? prices[0] : null;
      const pending = price === null;
      const cartTotal = this.cartSummary ? this.cartSummary.total : 0;

      if (!pending && el.classList.contains('is-updating')) this.flash(el, 'is-refreshed');
      el.classList.toggle('is-updating', pending);
      if (pending) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');

      const description = store
        ? `<span class="enova-minicart-sheet__method-desc">${escapeHTML(store.name)}</span>`
        : pending
          ? '<span class="enova-minicart-sheet__method-desc" style="width:112px">&nbsp;</span>'
          : '';
      const priceText = pending ? '&nbsp;' : escapeHTML(price ? formatMoney(price) : this.msg.msgFree);
      const totalText = pending ? '&nbsp;' : escapeHTML(formatMoney(cartTotal + price));
      el.innerHTML = `
        ${this.renderSummaryLines()}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgPickupLabel)}</span>
            ${description}
          </span>
          <span class="enova-minicart-sheet__method-price"${pending ? ' style="width:56px"' : ''}>${priceText}</span>
        </div>
        <div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value"${pending ? ' style="width:72px"' : ''}>${totalText}</span>
        </div>`;
    }

    // Tarjetas de tienda en skeleton (misma forma que las reales) mientras llegan las tiendas.
    renderStoresSkeleton() {
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const item = `
        <div class="enova-minicart-store is-skeleton" aria-hidden="true">
          <span class="enova-minicart-store__radio"></span>
          <span class="enova-minicart-store__text">${bar(128)}${bar(184)}</span>
          <span class="enova-minicart-store__price">${bar(36)}</span>
        </div>`;
      return `<span class="enova-minicart-sr">${escapeHTML(this.msg.msgLoading)}</span>${item.repeat(4)}`;
    }

    renderStores() {
      const wasSkeleton = !!this.storesEl.querySelector('.is-skeleton');
      // Con el contenido real ya puesto se suelta el alto conservado: la hoja pasa de una vez
      // al alto de las tiendas.
      this.releasePanelHeight(this.panels.pickup);
      if (wasSkeleton) this.flash(this.storesEl, 'is-revealed');
      if (!this.stores.length) {
        this.storesEl.innerHTML = `<p class="enova-minicart-stores__status">${escapeHTML(this.msg.msgStoresError)}</p>`;
        return;
      }

      const current = this.selectedStore();
      const preferred = current ? current.name : this.saved.pickup_store;
      const anySelectable = this.stores.some((store) => store.availability.selectable);

      let html = this.stores
        .map((store, index) => {
          const { selectable, missing, total } = store.availability;
          const place = [store.address, store.distrit].filter(Boolean).join(', ');
          const price = store.price_enabled && Number(store.price) > 0 ? formatPrice(store.price) : this.msg.msgFree;
          let note = store.complement ? `<span class="enova-minicart-store__meta">${escapeHTML(store.complement)}</span>` : '';
          if (!selectable) {
            const text =
              missing >= total
                ? this.msg.msgStoreUnavailable
                : missing === 1
                  ? this.msg.msgStoreMissingOne
                  : String(this.msg.msgStoreMissingOther || '').replace('__COUNT__', missing);
            note = `<span class="enova-minicart-store__stock">${escapeHTML(text)}</span>`;
          }
          const checked = selectable && store.name === preferred ? ' checked' : '';
          return `
            <label class="enova-minicart-store${selectable ? '' : ' is-disabled'}">
              <input type="radio" name="enova-minicart-store" value="${index}"${checked}${selectable ? '' : ' disabled'}>
              <span class="enova-minicart-store__radio" aria-hidden="true"></span>
              <span class="enova-minicart-store__text">
                <span class="enova-minicart-store__name">${escapeHTML(store.name)}</span>
                <span class="enova-minicart-store__address">${escapeHTML(place)}</span>
                ${note}
              </span>
              <span class="enova-minicart-store__price">${selectable ? escapeHTML(price) : ''}</span>
            </label>`;
        })
        .join('');

      if (!anySelectable) {
        html =
          `<div class="enova-minicart-stores__notice">
            <p>${escapeHTML(this.msg.msgPickupUnavailable)}</p>
            <button type="button" class="enova-minicart-stores__switch" data-enova-checkout-switch-delivery>
              ${escapeHTML(this.msg.msgSwitchDelivery)}
            </button>
          </div>` + html;
      }
      this.storesEl.innerHTML = html;
      this.renderPickupSummary();
      this.renderSaveLinks();
      if (this.compact.delivery && this.mode === 'pickup') {
        if (this.selectedStore()) this.renderSummary();
        else this.expand('delivery');
      }
    }

    selectedStore() {
      const input = this.storesEl.querySelector('input[name="enova-minicart-store"]:checked');
      return input ? this.stores[Number(input.value)] || null : null;
    }

    /* ---------------- Envío del formulario ---------------- */

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      Object.values(this.selects).forEach((select) => select.classList.remove('has-error'));
      this.storesEl.classList.remove('has-error');
      this.modesEl.classList.remove('has-error');
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      this.submitBtn.disabled = submitting;
      this.submitBtn.classList.toggle('is-loading', submitting);
      this.submitBtn.setAttribute('aria-busy', submitting ? 'true' : 'false');
      if (!this.defaultLabel) this.defaultLabel = this.submitLabel.textContent;
      this.submitLabel.textContent = submitting ? this.msg.msgProcessing : this.defaultLabel;
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      this.clearError();

      if (!this.mode) {
        this.modesEl.classList.add('has-error');
        this.showError(this.msg.msgModeRequired);
        return;
      }

      // Se valida todo de una vez; el mensaje es el del primer campo con error (de arriba abajo).
      const documentData = this.readDocument();
      const extra = documentData ? documentData.attributes : {};
      const order = this.mode === 'pickup' ? this.buildPickupOrder(extra) : this.buildDeliveryOrder(extra);
      if (!order || !documentData) {
        // Un error dentro del resumen abre esa sección para que se vea qué corregir.
        if (!order) this.expand('delivery');
        if (!documentData) this.expand('document');
        if (!documentData && this.errorEl.hidden) {
          this.showError(this.documentMessage);
          if (order) {
            this.invalidDocumentField.focus({ preventScroll: true });
            this.invalidDocumentField.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        return;
      }
      Object.assign(order.saved, documentData.saved);

      this.setSubmitting(true);
      this.goToCheckout(order).catch((error) => {
        console.warn('[enova-minicart] precheckout falló, se usa el checkout nativo', error);
        window.location.href = appendUTMs(`${root()}checkout`);
      });
    }

    buildDeliveryOrder(extra = {}) {
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      const missing = [
        ['region', region],
        ['province', province],
        ['district', district],
      ].filter(([, option]) => !option);

      if (missing.length) {
        missing.forEach(([level]) => this.selects[level].classList.add('has-error'));
        this.showError(this.msg.msgRequired);
        return null;
      }
      if (this.shippingEl.classList.contains('is-loading')) return null;

      const method = this.selectedMethod();
      if (!method) {
        this.showError(this.msg.msgUnavailable);
        return null;
      }

      const attributes = {
        country: 'Peru',
        dispatch_mode: 'delivery',
        region: region.value,
        province: province.value,
        district: district.value,
        ubigeo: district.dataset.code,
        shipping_method: method.code,
        ...extra,
      };

      return {
        attributes,
        line: {
          dispatchMode: 'delivery',
          description: method.effectiveDescription || '',
          shippingMethod: method.code,
          ubigeo: attributes.ubigeo,
        },
        buyerIdentity: {
          countryCode: COUNTRY,
          preferences: { delivery: { deliveryMethod: ['SHIPPING'] } },
          deliveryAddressPreferences: [{ deliveryAddress: buildDeliveryAddress(attributes, this.addressMapping) }],
        },
        saved: {
          dispatch_mode: 'delivery',
          region: attributes.region,
          province: attributes.province,
          district: attributes.district,
          shipping_method: attributes.shipping_method,
        },
      };
    }

    // Mismos atributos que el branch de retiro de handleCheckoutSubmit() en /cart.
    buildPickupOrder(extra = {}) {
      const store = this.selectedStore();
      if (!store || !store.availability.selectable) {
        this.storesEl.classList.add('has-error');
        this.showError(this.msg.msgStoreRequired);
        return null;
      }

      const attributes = {
        country: 'Peru',
        dispatch_mode: 'pickup',
        region: store.department || '',
        province: store.province || '',
        district: store.distrit || '',
        ubigeo: store.ubigeo || '',
        address: `Recojo Tienda: ${store.address || ''}`,
        reference: '-',
        pickup_store: store.name,
        ...extra,
      };

      // Facturación en retiro: los datos del cliente con el mismo mapeo que la dirección de envío
      // (documento o RUC en «DNI, CE o RUC»). Si eligió su ubigeo en esta sesión, también viaja;
      // el de la tienda no, porque no es la dirección del cliente.
      const billing = Object.assign({}, extra);
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      if (region && province && district && district.dataset.code) {
        Object.assign(billing, {
          region: region.value,
          province: province.value,
          district: district.value,
          ubigeo: district.dataset.code,
        });
      }

      // Sin deliveryAddress: en retiro cualquier dirección empuja el checkout a «Envío».
      const delivery = { deliveryMethod: ['PICK_UP'] };
      if (store.locationId) delivery.pickupHandle = [store.locationId];

      return {
        attributes,
        line: {
          dispatchMode: 'pickup',
          description: '',
          shippingMethod: 'regular',
          ubigeo: attributes.ubigeo,
        },
        // countryCode es obligatorio para que Shopify acepte la sucursal preferida
        // (el país del comprador debe coincidir con el de la location).
        buyerIdentity: { countryCode: COUNTRY, preferences: { delivery } },
        billingAddress: buildDeliveryAddress(billing, this.addressMapping),
        saved: {
          dispatch_mode: 'recojo-tienda',
          pickup_store: store.name,
        },
      };
    }

    goToCheckout(order) {
      writeCustomerData(order.saved);
      // Sin vacíos en los note_attributes (igual que /cart).
      Object.keys(order.attributes).forEach((key) => {
        if (String(order.attributes[key] || '').trim() === '') delete order.attributes[key];
      });

      return fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: order.attributes }),
      })
        .then((response) => response.json())
        .then((cart) => this.createCheckout(cart, order))
        .then((checkoutUrl) => {
          window.location.href = appendUTMs(appendBillingAddress(checkoutUrl, order.billingAddress));
        });
    }

    createCheckout(cart, order) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        return Promise.reject(new Error('Carrito vacío'));
      }

      const lines = cart.items.map((item) => {
        const existing = Object.entries(item.properties || {}).map(([key, value]) => ({ key, value: String(value) }));
        return {
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
          attributes: existing.concat([
            { key: '_dispatch_mode', value: order.line.dispatchMode },
            { key: '_dispatch_mode_description', value: String(order.line.description) },
            { key: '_shipping_method', value: String(order.line.shippingMethod) },
            { key: '_ubigeo', value: String(order.line.ubigeo) },
            { key: '_peso', value: String(item.grams) },
            { key: '_sku', value: String(item.sku || '') },
          ]),
        };
      });

      const discountCode = getAppliedDiscountCode(cart);
      const input = {
        attributes: Object.keys(order.attributes).map((key) => ({ key, value: String(order.attributes[key]) })),
        lines,
        buyerIdentity: order.buyerIdentity,
      };
      if (discountCode) input.discountCodes = [discountCode];

      return storefront(CART_CREATE, { input }).then((response) => {
        const result = response && response.data && response.data.cartCreate;
        const errors = (response && response.errors) || (result && result.userErrors) || [];
        if (!result || !result.cart || errors.length) {
          throw new Error((errors[0] && errors[0].message) || 'cartCreate sin carrito');
        }
        return result.cart.checkoutUrl;
      });
    }
  }

  function init() {
    captureUTMs();
    document.querySelectorAll('.enova-minicart').forEach((drawer) => {
      if (drawer.dataset.enovaCheckoutReady === 'true') return;
      if (!drawer.querySelector('[data-enova-checkout-sheet]')) return;
      drawer.dataset.enovaCheckoutReady = 'true';
      new EnovaMinicartCheckout(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
